import React from 'react';
import { IonBackButton, IonButtons, IonContent, IonHeader, IonPage, IonTitle, IonToolbar } from '@ionic/react';
import './LegalPage.css';

type LegalPageProps = { kind: 'privacy' | 'imprint' };

const PrivacyContent = () => (
  <>
    <p className="legal-status">Draft — complete and review before publishing</p>
    <h2>Privacy policy</h2>
    <p>Last updated: 23 August 2026</p>
    <h3>1. Controller</h3>
    <p>[Full legal name]<br />[Serviceable postal address]<br />[Email address]</p>
    <h3>2. What stays on your device</h3>
    <p>Your workouts, sets, routines, calendar logs, statistics and fitness profile are stored locally. In the installed native app this uses SQLite; in the web app it uses your browser&apos;s IndexedDB. PawerLifting does not intentionally upload this fitness data to its servers.</p>
    <p>You can export a JSON backup or delete the local records from User Area. Browser data may be removed by browser or device cleanup, so keep a private backup if the web app is important to you.</p>
    <h3>3. Essential device storage</h3>
    <p>PawerLifting uses localStorage, sessionStorage and IndexedDB where technically necessary to remember privacy and security choices, keep an active workout available, support PWA operation and store local fitness records. These technologies are not used for advertising or cross-site tracking.</p>
    <h3>4. Account and machine catalogue</h3>
    <p>Firebase Authentication processes the email address, account identifier, authentication metadata and security-related technical information needed to create and protect an account. A verification email is sent after registration. The shared machine catalogue is loaded from Cloud Firestore and is not intended to contain personal workout data.</p>
    <p>[Before launch: add the legal basis, retention periods, Google/Firebase processing details, applicable data-processing agreement and information about possible international transfers.]</p>
    <h3>5. External media and consent</h3>
    <p>YouTube execution videos are optional. If external media is refused, the player is not loaded automatically. A user may still explicitly load an individual video. The privacy choice can be changed through “Privacy settings” on the public page. Loading YouTube may disclose the IP address and browser/device information to Google and may create or access device storage.</p>
    <h3>6. Hosting and technical logs</h3>
    <p>The web app is delivered through Firebase Hosting. In order to deliver and secure the website, the hosting provider may process technical request and log information such as IP address, request time, requested resource, referrer, browser and operating system information.</p>
    <p>[Before launch: add the precise legal basis, Firebase/Google entity, retention period, processing region and transfer safeguards.]</p>
    <h3>7. Your choices</h3>
    <p>Essential storage cannot be disabled through the consent dialog because the selected functions require it. External media can be refused without losing access to the workout log and machine catalogue. Browser storage can also be removed through browser settings, but doing so may erase local fitness records and preferences.</p>
    <h3>8. Your rights</h3>
    <p>[Add access, rectification, erasure, restriction, portability, objection, consent withdrawal and the right to complain to the competent supervisory authority.]</p>
    <h3>9. Fitness information</h3>
    <p>PawerLifting is a fitness log, not a medical device. Do not enter medical diagnoses or other information you do not need for training.</p>
  </>
);

const ImprintContent = () => (
  <>
    <p className="legal-status">Draft — replace every placeholder before publishing</p>
    <h2>Imprint / Anbieterkennzeichnung</h2>
    <h3>Provider</h3>
    <p>[Full legal name]<br />[Serviceable postal address in Germany]</p>
    <h3>Contact</h3>
    <p>Email: [contact email]<br />Telephone: [add if required for direct communication]</p>
    <h3>Responsible for content</h3>
    <p>[Only add the legally required responsible person if the app contains journalistic-editorial content.]</p>
    <h3>Consumer dispute resolution</h3>
    <p>[Check whether a consumer dispute-resolution notice applies to your final offering.]</p>
    <h3>Important</h3>
    <p>Do not invent details or publish this placeholder. Whether additional information is required depends on how the app is offered and monetised.</p>
  </>
);

const LegalPage: React.FC<LegalPageProps> = ({ kind }) => (
  <IonPage>
    <IonHeader>
      <IonToolbar>
        <IonButtons slot="start"><IonBackButton defaultHref="/my/settings" /></IonButtons>
        <IonTitle>{kind === 'privacy' ? 'Privacy' : 'Imprint'}</IonTitle>
      </IonToolbar>
    </IonHeader>
    <IonContent fullscreen className="legal-content">
      <article className="legal-document">{kind === 'privacy' ? <PrivacyContent /> : <ImprintContent />}</article>
    </IonContent>
  </IonPage>
);

export default LegalPage;
